/**
 * Pagination footer — Preline datatable style (rounded controls + row count).
 */

import React from 'react'

const PAGE_BTN =
  'min-w-10 flex justify-center items-center py-2.5 text-sm rounded-full text-gray-800 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none'

const NAV_BTN =
  'p-2.5 min-w-10 inline-flex justify-center items-center gap-x-2 text-sm rounded-full text-gray-800 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none'

export default function DatatablePagination({
  page,
  pageCount,
  totalItems,
  showingFrom,
  showingTo,
  goToPage,
  goPrev,
  goNext,
  hasPrev,
  hasNext,
  showPagination,
  itemLabel = 'results',
}) {
  if (totalItems === 0) return null

  const pages = []
  const maxVisible = 5
  let start = Math.max(0, page - Math.floor(maxVisible / 2))
  let end = Math.min(pageCount - 1, start + maxVisible - 1)
  start = Math.max(0, end - maxVisible + 1)

  for (let i = start; i <= end; i += 1) pages.push(i)

  const showNav = showPagination && pageCount > 1

  return (
    <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-gray-500">
        Showing{' '}
        <span className="font-medium text-gray-800">{showingFrom}</span> to{' '}
        <span className="font-medium text-gray-800">{showingTo}</span> of{' '}
        <span className="font-medium text-gray-800">{totalItems}</span> {itemLabel}
      </p>

      {showNav ? (
        <nav className="flex items-center gap-1" aria-label="Table pagination">
          <button type="button" onClick={goPrev} disabled={!hasPrev} className={NAV_BTN}>
            <span aria-hidden="true">«</span>
            <span className="sr-only">Previous</span>
          </button>

          <div className="flex items-center gap-1">
            {start > 0 && (
              <>
                <button type="button" onClick={() => goToPage(0)} className={PAGE_BTN}>
                  1
                </button>
                {start > 1 ? <span className="px-1 text-gray-400">…</span> : null}
              </>
            )}
            {pages.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => goToPage(p)}
                className={`${PAGE_BTN} ${p === page ? 'bg-gray-200 font-medium' : ''}`}
                aria-current={p === page ? 'page' : undefined}
              >
                {p + 1}
              </button>
            ))}
            {end < pageCount - 1 && (
              <>
                {end < pageCount - 2 ? <span className="px-1 text-gray-400">…</span> : null}
                <button type="button" onClick={() => goToPage(pageCount - 1)} className={PAGE_BTN}>
                  {pageCount}
                </button>
              </>
            )}
          </div>

          <button type="button" onClick={goNext} disabled={!hasNext} className={NAV_BTN}>
            <span className="sr-only">Next</span>
            <span aria-hidden="true">»</span>
          </button>
        </nav>
      ) : null}
    </div>
  )
}
